import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getTeamMembers, getProjects, getAllocations, createAllocation, deleteAllocation, bulkUpsertAllocations, getTimeOffSemana, getHolidays, getEventos, getEmailAuthStatus, getEngineerSendPreview, sendEngineerPlans, getPMSendPreview, sendPMPlans, sendWeekExcel, getSentWeekAllocations, reorderTeamMembers, reorderProjects, saveWeek, clearWeekAllocations, getWeekLimitUsage, getWeekBudgetUsage } from '../api/client'
import { currentWeekMonday, addWeeks, formatWeekRange } from '../utils/weeks'
import Modal from '../components/Modal'

const TYPE_DOT_CLS = { PTO: 'bg-emerald-400', medical: 'bg-red-400', work_trip: 'bg-blue-400', admin: 'bg-amber-400', permiso: 'bg-violet-400' }
const TYPE_RING_CLS = { PTO: 'ring-emerald-400', medical: 'ring-red-400', work_trip: 'ring-blue-400', admin: 'ring-amber-400', permiso: 'ring-violet-400' }
const TYPE_LABELS = { PTO: 'PTO', medical: 'Medical', work_trip: 'Work Trip', admin: 'Administrative Leave', permiso: 'Permiso' }

// A leave's tooltip: type, dates, which half, and whether it's still pending
// (shown, never deducted -- only approved leave takes hours).
const fechaCorta = (iso) => { const [, m, d] = iso.split('-'); return `${d}/${m}` }
function leaveTitle(t) {
  const fechas = t.start_date === t.end_date ? fechaCorta(t.start_date) : `${fechaCorta(t.start_date)}–${fechaCorta(t.end_date)}`
  const mitad = t.half_day ? (t.mitad === 'am' ? ' · AM' : t.mitad === 'pm' ? ' · PM' : ' · ½') : ''
  return `${TYPE_LABELS[t.type] || t.type} · ${fechas}${mitad}${t.estado === 'pendiente' ? ' · pendiente' : ''}`
}
const leaveDotCls = (t) => t.estado === 'pendiente'
  ? `bg-white ring-1 ${TYPE_RING_CLS[t.type] || 'ring-gray-300'}`
  : (TYPE_DOT_CLS[t.type] || 'bg-gray-300')

// An ISO date plus n days, as an ISO date.
function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fmtHours(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

function utilColor(total, max) {
  if (total <= 0 || max <= 0) return null
  if (total > max) return 'red'
  if (total >= max - 0.5) return 'green'
  return 'amber'
}

function UtilBar({ used, max }) {
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0
  const c = utilColor(used, max)
  const barCls = c === 'red' ? 'bg-red-500' : c === 'green' ? 'bg-emerald-500' : c === 'amber' ? 'bg-amber-400' : 'bg-gray-200'
  const txtCls = c === 'red' ? 'text-red-500' : c === 'green' ? 'text-emerald-600' : c === 'amber' ? 'text-amber-600' : 'text-gray-400'
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5 min-w-16 overflow-hidden">
        <div className={`h-1.5 rounded-full transition-all ${barCls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-medium tabular-nums w-16 text-right shrink-0 ${txtCls}`}>
        {used}h / {max}h
      </span>
      {c === 'red' && <span className="text-red-500 text-xs shrink-0">⚠</span>}
    </div>
  )
}

export default function PlanWeek() {
  const week = addWeeks(currentWeekMonday(), 1) // always plan next week
  const [tab, setTab] = useState('grid') // 'grid' | 'project' | 'engineer'

  const [members, setMembers] = useState([])
  const [projects, setProjects] = useState([])
  const [allocations, setAllocations] = useState([])
  // {member id: {disponibles, ausencias}} for this week (routers/time_off.semana)
  const [semanaInfo, setSemanaInfo] = useState({})
  const [holidays, setHolidays] = useState([])
  const [eventos, setEventos] = useState([])     // company events this week (Navidad, paseo...)
  const [weekLimits, setWeekLimits] = useState(null) // WeekLimitUsageResponse | null
  const [weekBudgets, setWeekBudgets] = useState(null) // WeekBudgetUsageResponse | null
  const [loading, setLoading] = useState(true)

  const [expandedProjects, setExpandedProjects] = useState({})
  const [expandedEngineers, setExpandedEngineers] = useState({})

  // Send email state
  const [showSendModal, setShowSendModal] = useState(false)
  const [sendMessage, setSendMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState(null) // EngineerPlanEmailResponse | null
  const [authStatus, setAuthStatus] = useState(null) // null=loading, { authorized: bool }
  const [engineerPreview, setEngineerPreview] = useState([])
  const [checkedEngineers, setCheckedEngineers] = useState({})

  // PM data (loaded together with engineers in the unified send modal)
  const [pmPreview, setPMPreview] = useState([])
  const [pmMembers, setPMMembers] = useState([])   // all team members for email lookup
  const [pmEmails, setPMEmails] = useState({})     // pm_name -> email
  const [checkedPMs, setCheckedPMs] = useState({}) // pm_name -> bool
  const [pmResult, setPMResult] = useState(null)

  const [editing, setEditing] = useState(null) // { memberId, projectId }
  const [editVal, setEditVal] = useState('')
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedFlash, setSavedFlash] = useState(false)
  const [clearing, setClearing] = useState(false)

  // Drag-to-reorder state
  const [memberOrder, setMemberOrder] = useState([])
  const [projectOrder, setProjectOrder] = useState([])
  const [dragId, setDragId] = useState(null)
  const [dragType, setDragType] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)

  const { t } = useTranslation('engineering')

  const loadSeq = useRef(0)

  const load = useCallback(async () => {
    const seq = ++loadSeq.current
    setLoading(true)
    const [mems, projs, allocs, tos, hols, evs, limits, budgets] = await Promise.all([
      // The grid plans hours, so only people whose hours are planned.
      getTeamMembers({ plannable: true }),
      getProjects(),
      getAllocations({ week_start: week }),
      getTimeOffSemana(week).catch(() => ({})),
      getHolidays({ week_start: week }),
      getEventos({ desde: week, hasta: addDaysISO(week, 4) }).catch(() => []),
      // Admin-only, like this page. A failure here must not blank the grid, so
      // both degrade to no warnings rather than an error.
      getWeekLimitUsage(week).catch(() => null),
      getWeekBudgetUsage(week).catch(() => null),
    ])
    if (seq !== loadSeq.current) return // superseded by a newer load
    setMembers(mems)
    setProjects(projs.filter(p => p.is_active && !p.is_purchase_only))
    setAllocations(allocs)
    setSemanaInfo(tos)
    setHolidays(hols)
    setEventos(evs)
    setWeekLimits(limits)
    setWeekBudgets(budgets)
    setLoading(false)
  }, [week])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    setMemberOrder(prev => {
      const ids = members.map(m => m.id)
      return [...prev.filter(id => ids.includes(id)), ...ids.filter(id => !prev.includes(id))]
    })
  }, [members])

  useEffect(() => {
    setProjectOrder(prev => {
      const ids = projects.map(p => p.id)
      return [...prev.filter(id => ids.includes(id)), ...ids.filter(id => !prev.includes(id))]
    })
  }, [projects])

  // --- Helpers ---
  const getAllocHours = (memberId, projectId) =>
    allocations.find(a => a.team_member_id === memberId && a.project_id === projectId)?.hours ?? 0

  const getTotalHours = (memberId) =>
    allocations.filter(a => a.team_member_id === memberId).reduce((s, a) => s + a.hours, 0)

  const getProjectTotal = (projectId) =>
    allocations.filter(a => a.project_id === projectId).reduce((s, a) => s + a.hours, 0)

  const getMemberTimeOff = (memberId) => semanaInfo[memberId]?.ausencias || []

  const orderedMembers  = memberOrder.map(id => members.find(m => m.id === id)).filter(Boolean)
  const orderedProjects = projectOrder.map(id => projects.find(p => p.id === id)).filter(Boolean)

  const startDrag = (e, id, type) => {
    setDragId(id); setDragType(type)
    e.dataTransfer.effectAllowed = 'move'
  }
  const onDragOver = (e, id) => { e.preventDefault(); setDragOverId(id) }
  const onDrop = (e, targetId, type) => {
    e.preventDefault(); setDragOverId(null)
    if (dragId === targetId) { setDragId(null); return }
    const isMembers = type === 'member'
    const currentOrder = isMembers ? memberOrder : projectOrder
    const arr = [...currentOrder]
    const fi = arr.indexOf(dragId), ti = arr.indexOf(targetId)
    if (fi === -1 || ti === -1) { setDragId(null); return }
    arr.splice(fi, 1)
    arr.splice(ti, 0, dragId)
    if (isMembers) {
      setMemberOrder(arr)
      reorderTeamMembers(arr).catch(() => {})
    } else {
      setProjectOrder(arr)
      reorderProjects(arr).catch(() => {})
    }
    setDragId(null)
  }
  const onDragEnd = () => { setDragId(null); setDragOverId(null) }

  const sortMembersAlpha = () => {
    const sorted = [...orderedMembers]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(m => m.id)
    setMemberOrder(sorted)
    reorderTeamMembers(sorted).catch(() => {})
  }

  // Hours left this week: the server counts it like Capacity does -- the
  // person's own day length, approved leave only, a holiday inside a
  // vacation once, half days as halves.
  const getEffectiveMaxHours = (member) => semanaInfo[member.id]?.disponibles ?? member.max_weekly_hours

  // --- Inline editing ---
  const startEdit = (memberId, projectId) => {
    const cur = getAllocHours(memberId, projectId)
    setEditing({ memberId, projectId })
    setEditVal(cur === 0 ? '' : String(cur))
  }

  const commitEdit = async () => {
    if (!editing) return
    const hours = parseFloat(editVal) || 0
    const existing = allocations.find(
      a => a.team_member_id === editing.memberId && a.project_id === editing.projectId
    )
    if (hours === 0 && existing) {
      await deleteAllocation(existing.id)
    } else if (hours > 0) {
      await createAllocation({
        team_member_id: editing.memberId,
        project_id: editing.projectId,
        week_start: week,
        hours,
      })
    }
    setEditing(null)
    setEditVal('')
    const allocs = await getAllocations({ week_start: week })
    setAllocations(allocs)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') { setEditing(null); setEditVal('') }
  }

  // --- Send email ---
  const checkAuthStatus = async () => {
    try {
      const status = await getEmailAuthStatus()
      setAuthStatus(status)
    } catch {
      setAuthStatus({ authorized: false })
    }
  }

  const openSendModal = async () => {
    setSendMessage('')
    setSendResult(null)
    setPMResult(null)
    setAuthStatus(null)
    setEngineerPreview([])
    setCheckedEngineers({})
    setPMPreview([])
    setPMEmails({})
    setCheckedPMs({})
    setShowSendModal(true)
    checkAuthStatus()
    try {
      const [engPreview, pmPrev, allMembers] = await Promise.all([
        getEngineerSendPreview(week),
        getPMSendPreview(week),
        getTeamMembers(),
      ])
      setEngineerPreview(engPreview)
      const engChecks = {}
      for (const eng of engPreview) {
        engChecks[eng.id] = !!(eng.email && eng.total_hours > 0)
      }
      setCheckedEngineers(engChecks)

      setPMPreview(pmPrev)
      setPMMembers(allMembers)
      const emailMap = {}
      const pmChecks = {}
      for (const pm of pmPrev) {
        const matched = allMembers.find(
          m => m.name.toLowerCase() === pm.pm_name.toLowerCase() && m.email
        )
        const resolved = matched?.email || pm.suggested_email || ''
        emailMap[pm.pm_name] = resolved
        pmChecks[pm.pm_name] = !!resolved
      }
      setPMEmails(emailMap)
      setCheckedPMs(pmChecks)
    } catch {}
  }

  const toggleEngineer = (id) => {
    setCheckedEngineers(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const togglePM = (name) => {
    setCheckedPMs(prev => ({ ...prev, [name]: !prev[name] }))
  }

  const handleSave = async () => {
    if (totalHours === 0) return
    setSaving(true)
    try {
      await saveWeek(week, false)
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1800)
    } catch {}
    setSaving(false)
  }

  const handleClear = async () => {
    if (totalHours === 0) return
    if (!confirm(t('planWeek.confirmClear', { week: formatWeekRange(week) }))) return
    setClearing(true)
    try {
      await clearWeekAllocations(week)
      await load()
    } catch {}
    setClearing(false)
  }

  const handleSend = async () => {
    const memberIds = Object.entries(checkedEngineers)
      .filter(([, checked]) => checked)
      .map(([id]) => parseInt(id))
    const pmRecipients = Object.entries(checkedPMs)
      .filter(([, c]) => c)
      .map(([name]) => ({ pm_name: name, email: (pmEmails[name] || '').trim() }))
      .filter(r => r.email && r.email.includes('@'))

    if (memberIds.length === 0 && pmRecipients.length === 0) return

    setSending(true)
    setSendResult(null)
    setPMResult(null)

    const tasks = []
    let engResult = null
    let pmRes = null
    if (memberIds.length > 0) {
      tasks.push(
        sendEngineerPlans({
          week_start: week,
          member_ids: memberIds,
          message: sendMessage || null,
        })
          .then(res => { engResult = res; setSendResult(res) })
          .catch(e => setSendResult({ error: e.response?.data?.detail || 'Failed to send engineer emails' }))
      )
    }
    if (pmRecipients.length > 0) {
      tasks.push(
        sendPMPlans({
          week_start: week,
          recipients: pmRecipients,
          message: sendMessage || null,
        })
          .then(res => { pmRes = res; setPMResult(res) })
          .catch(e => setPMResult({ error: e.response?.data?.detail || 'Failed to send PM emails' }))
      )
    }

    await Promise.all(tasks)

    // Background: email a copy of the planning Excel to the archive mailbox.
    // Best-effort and fire-and-forget — must not block the UI or affect the
    // visible send result. One copy per Send click.
    if ((engResult?.sent_count ?? 0) > 0 || (pmRes?.sent_count ?? 0) > 0) {
      sendWeekExcel({ week_start: week }).catch(() => {})
    }

    // sendEngineerPlans clears the week server-side after a successful send,
    // so reload to reflect that.
    await load()
    setSending(false)
  }

  const loadPrevious = async () => {
    setLoadingPrev(true)
    const prevWeek = addWeeks(week, -1)
    let source = await getAllocations({ week_start: prevWeek })
    if (source.length === 0) {
      // Live rows may have been cleared after send — fall back to the snapshot
      source = await getSentWeekAllocations(prevWeek).catch(() => [])
    }
    if (source.length > 0) {
      const items = source.map(a => ({
        team_member_id: a.team_member_id,
        project_id: a.project_id,
        week_start: week,
        hours: a.hours,
      }))
      await bulkUpsertAllocations(items)
      await load()
    } else {
      alert(t('planWeek.noAllocsForPrevWeek'))
    }
    setLoadingPrev(false)
  }

  // --- Weekly hour limits (set by admin/C-level in Management → Limits) ---
  // The limit is a rate per week, and the hours for the week on screen are in
  // this page's own state, so the warning tracks live edits with no refetch.
  const weekUsage = (projectId) => {
    const row = weekLimits?.projects?.find(p => p.project_id === projectId)
    if (!row) return null
    const planned = getProjectTotal(projectId)
    const over = row.max_weekly_hours != null && planned > row.max_weekly_hours
    // A project nobody is staffed on this week is idle, not short-staffed.
    const under = !over && row.min_weekly_hours != null
      && planned > 0 && planned < row.min_weekly_hours
    if (!over && !under) return null
    const limit = over ? row.max_weekly_hours : row.min_weekly_hours
    return { planned, over, under, limit, gap: Math.abs(planned - limit) }
  }

  // --- Lifetime hours budget (set per project in its Presupuesto tab) ---
  // A different question from the weekly band above: a week can sit perfectly
  // inside its rate and still be the week that exhausts the project. What the
  // other weeks already took comes from the server; this week's figure is the
  // page's own live state, so the warning tracks edits with no refetch.
  const NEAR_BUDGET = 0.9

  const budgetUsage = (projectId) => {
    const row = weekBudgets?.projects?.find(p => p.project_id === projectId)
    if (!row) return null
    const planned = getProjectTotal(projectId)
    const projected = row.used_other_weeks + planned
    const over = projected > row.hours_budget
    // "About to" run out is the point of the warning, so flag the approach
    // too -- but only once this week actually puts hours on the project.
    const near = !over && planned > 0
      && projected >= row.hours_budget * NEAR_BUDGET
    if (!over && !near) return null
    return {
      planned, projected, over, near,
      budget: row.hours_budget,
      remainingBefore: row.remaining_before,
      gap: Math.abs(projected - row.hours_budget),
      percent: row.hours_budget > 0
        ? Math.round((projected / row.hours_budget) * 100) : null,
    }
  }

  const budgetProjects = orderedProjects
    .map(p => ({ project: p, usage: budgetUsage(p.id) }))
    .filter(x => x.usage)
  const overBudgetProjects = budgetProjects.filter(x => x.usage.over)
  const nearBudgetProjects = budgetProjects.filter(x => x.usage.near)

  const budgetTooltip = (usage) =>
    t(usage.over ? 'planWeek.budgetOverTooltip' : 'planWeek.budgetNearTooltip', {
      projected: fmtHours(usage.projected),
      budget: fmtHours(usage.budget),
      gap: fmtHours(usage.gap),
      remaining: fmtHours(usage.remainingBefore),
      percent: usage.percent,
    })

  const nameWithBudget = ({ project, usage }) =>
    `${project.nickname || project.name} (${fmtHours(usage.projected)}h / ${fmtHours(usage.budget)}h)`

  const limitProjects = orderedProjects
    .map(p => ({ project: p, usage: weekUsage(p.id) }))
    .filter(x => x.usage)
  const overLimitProjects = limitProjects.filter(x => x.usage.over)
  const underLimitProjects = limitProjects.filter(x => x.usage.under)

  const limitTooltip = (usage) =>
    t(usage.over ? 'planWeek.weekOverTooltip' : 'planWeek.weekUnderTooltip', {
      planned: fmtHours(usage.planned),
      limit: fmtHours(usage.limit),
      gap: fmtHours(usage.gap),
    })

  const nameWithHours = ({ project, usage }) =>
    `${project.nickname || project.name} (${fmtHours(usage.planned)}h / ${fmtHours(usage.limit)}h)`

  // --- Derived stats ---
  const overLimitMembers = orderedMembers.filter(m => getTotalHours(m.id) > getEffectiveMaxHours(m))
  const totalHours = allocations.reduce((s, a) => s + a.hours, 0)
  const plannedMembers = new Set(allocations.map(a => a.team_member_id)).size

  // A quiet marker next to a project name: red when the month is over its
  // maximum, amber while it is still short of its minimum. Projects with no
  // limit set for the month render nothing at all.
  const LimitMarker = ({ projectId, className = '' }) => {
    const usage = weekUsage(projectId)
    if (!usage) return null
    return (
      <span
        title={limitTooltip(usage)}
        className={`shrink-0 text-xs ${usage.over ? 'text-red-500' : 'text-amber-500'} ${className}`}
      >
        {usage.over ? '⚠' : '↓'}
      </span>
    )
  }

  // The budget marker sits beside the weekly-limit one and deliberately uses a
  // different glyph: they answer different questions and a project can trip
  // one without the other. Σ reads as "the accumulated total", which is what
  // a lifetime budget is measured against.
  const BudgetMarker = ({ projectId, className = '' }) => {
    const usage = budgetUsage(projectId)
    if (!usage) return null
    return (
      <span
        title={budgetTooltip(usage)}
        className={`shrink-0 text-xs ${usage.over ? 'text-red-600' : 'text-orange-500'} ${className}`}
      >
        Σ
      </span>
    )
  }

  const ProjectLimitBanners = () => (
    <>
      {overLimitProjects.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <span className="text-red-500">⚠</span>
          <div className="text-sm text-red-700">
            <strong className="mr-1">
              {t('planWeek.weekOverWarning', {
                count: overLimitProjects.length,
              })}
            </strong>
            {overLimitProjects.map(nameWithHours).join(', ')}
          </div>
        </div>
      )}
      {underLimitProjects.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <span className="text-amber-500">↓</span>
          <div className="text-sm text-amber-700">
            <strong className="mr-1">
              {t('planWeek.weekUnderWarning', {
                count: underLimitProjects.length,
              })}
            </strong>
            {underLimitProjects.map(nameWithHours).join(', ')}
          </div>
        </div>
      )}
      {overBudgetProjects.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <span className="text-red-600">Σ</span>
          <div className="text-sm text-red-700">
            <strong className="mr-1">
              {t('planWeek.budgetOverWarning', { count: overBudgetProjects.length })}
            </strong>
            {overBudgetProjects.map(nameWithBudget).join(', ')}
          </div>
        </div>
      )}
      {nearBudgetProjects.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <span className="text-orange-500">Σ</span>
          <div className="text-sm text-orange-700">
            <strong className="mr-1">
              {t('planWeek.budgetNearWarning', { count: nearBudgetProjects.length })}
            </strong>
            {nearBudgetProjects.map(nameWithBudget).join(', ')}
          </div>
        </div>
      )}
    </>
  )

  // Shared hour input cell
  const HourCell = ({ memberId, projectId }) => {
    const hoursOnProject = getAllocHours(memberId, projectId)
    const hasAlloc = hoursOnProject > 0
    const isEditing = editing?.memberId === memberId && editing?.projectId === projectId
    if (isEditing) {
      return (
        <input
          autoFocus
          type="number"
          min="0"
          max="80"
          step="0.5"
          className="w-20 text-center border border-gray-400 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          value={editVal}
          onChange={e => setEditVal(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
        />
      )
    }
    return (
      <button
        onClick={() => startEdit(memberId, projectId)}
        className={`w-20 h-[30px] inline-flex items-center justify-center rounded-lg px-2 text-sm transition-colors border ${
          hasAlloc
            ? 'bg-gray-100 border-gray-200 text-gray-900 font-semibold hover:bg-gray-200'
            : 'bg-white border-gray-200 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        {hasAlloc ? `${hoursOnProject}h` : ' '}
      </button>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">
            {t('planWeek.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{formatWeekRange(week)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadPrevious}
            disabled={loadingPrev}
            className="border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('planWeek.loadPreviousTitle')}
          >
            {loadingPrev ? t('planWeek.loadingPrev') : t('planWeek.loadPrevious')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="border border-gray-200 bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('planWeek.saveTitle')}
          >
            {saving ? t('planWeek.saving') : savedFlash ? t('planWeek.saved') : t('planWeek.save')}
          </button>
          <button
            onClick={openSendModal}
            className="flex items-center gap-1.5 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
            title={t('planWeek.sendTitle')}
          >
            {t('planWeek.sendPlan')}
          </button>
        </div>
      </div>

      {/* Top row: tabs + summary chips + holiday chips on one line */}
      {!loading && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl shrink-0">
            <button
              onClick={() => setTab('grid')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t('planWeek.tabGrid')}
              {overLimitMembers.length > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-xs rounded-full w-4 h-4 inline-flex items-center justify-center">
                  {overLimitMembers.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab('project')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'project' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t('planWeek.tabByProject')}
            </button>
            <button
              onClick={() => setTab('engineer')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'engineer' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t('planWeek.tabByEngineer')}
            </button>
          </div>

          <button
            onClick={sortMembersAlpha}
            className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors shrink-0"
            title={t('planWeek.sortAZ')}
          >
            <span className="text-xs">↑↓</span> {t('planWeek.sortAZ')}
          </button>

          <div className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-sm font-medium">
            {t('planWeek.hoursPlanned', {count: totalHours})}
          </div>
          <div className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full text-sm font-medium">
            {t('planWeek.engineers', {planned: plannedMembers, total: members.length})}
          </div>
          {overLimitMembers.length > 0 ? (
            <div className="bg-red-100 text-red-600 px-3 py-1.5 rounded-full text-sm font-medium">
              {t('planWeek.overLimit', {count: overLimitMembers.length})}
            </div>
          ) : totalHours > 0 ? (
            <div className="bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-sm font-medium">
              {t('planWeek.noOvertime')}
            </div>
          ) : null}

          {holidays.map(h => (
            <span key={h.id} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
              {h.name} <span className="opacity-60">({h.date})</span>
            </span>
          ))}
          {/* Company events: a day off already counts in everyone's hours (like a holiday). */}
          {eventos.map(e => (
            <span key={`ev-${e.id}`} title={e.notas || undefined}
              className={`text-xs px-2.5 py-1 rounded-full font-medium ${e.libre
                ? 'bg-violet-100 text-violet-700' : 'border border-violet-200 text-violet-700'}`}>
              {e.nombre} <span className="opacity-60">({e.start_date === e.end_date ? e.start_date : `${e.start_date} – ${e.end_date}`}{e.mitad ? ` · ${e.mitad.toUpperCase()}` : ''})</span>
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm py-8">{t('planWeek.loadingPrev')}</div>
      ) : members.length === 0 || projects.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{t('planWeek.noMembersOrProjects')}</p>
        </div>
      ) : tab === 'grid' ? (

        /* ─── PLAN BY GRID ─── */
        <div>
          {overLimitMembers.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-3">
              <span className="text-red-500">⚠</span>
              <div className="text-sm text-red-700">
                <strong>{t('planWeek.overLimitWarning', {count: overLimitMembers.length})}</strong>
                {overLimitMembers.map(m => `${m.name} (${getTotalHours(m.id)}h / ${getEffectiveMaxHours(m)}h)`).join(', ')}
              </div>
            </div>
          )}

          <ProjectLimitBanners />

          <div className="overflow-auto rounded-xl border border-gray-200" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead className="sticky top-0 z-20">
                <tr>
                  <th className="sticky left-0 bg-gray-50 border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700 min-w-56 z-30">
                    {t('planWeek.engineerColHeader')}
                  </th>
                  {orderedProjects.map(p => (
                    <th
                      key={p.id}
                      draggable
                      onDragStart={e => startDrag(e, p.id, 'project')}
                      onDragOver={e => onDragOver(e, p.id)}
                      onDrop={e => onDrop(e, p.id, 'project')}
                      onDragEnd={onDragEnd}
                      title={p.name}
                      className={`border border-gray-200 px-3 py-3 text-center font-semibold text-gray-700 min-w-28 cursor-grab select-none transition-colors ${dragId === p.id ? 'opacity-40' : ''} ${dragOverId === p.id && dragType === 'project' ? 'bg-gray-100' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                        <span className="truncate max-w-20 text-xs">{p.nickname || p.name}</span>
                        <LimitMarker projectId={p.id} />
                        <BudgetMarker projectId={p.id} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderedMembers.map(member => {
                  const total = getTotalHours(member.id)
                  const effectiveMax = getEffectiveMaxHours(member)
                  const c = utilColor(total, effectiveMax)
                  const memberTOs = getMemberTimeOff(member.id)
                  const rowBg = c === 'red' ? 'bg-red-50' : c === 'green' ? 'bg-emerald-50' : c === 'amber' ? 'bg-amber-50/60' : 'bg-gray-50/50'
                  // Opaque variant for the sticky name column so scrolled cells don't show through
                  const stickyBg = c === 'red' ? 'bg-red-50' : c === 'green' ? 'bg-emerald-50' : c === 'amber' ? 'bg-amber-50' : 'bg-white'
                  const txtCls = c === 'red' ? 'text-red-600' : c === 'green' ? 'text-emerald-600' : c === 'amber' ? 'text-amber-600' : 'text-gray-300'
                  const isDragging = dragId === member.id
                  const isDragOver = dragOverId === member.id && dragType === 'member'

                  return (
                    <tr
                      key={member.id}
                      draggable
                      onDragStart={e => startDrag(e, member.id, 'member')}
                      onDragOver={e => onDragOver(e, member.id)}
                      onDrop={e => onDrop(e, member.id, 'member')}
                      onDragEnd={onDragEnd}
                      className={`${rowBg} ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'outline outline-2 outline-gray-400' : ''}`}
                    >
                      <td className={`sticky left-0 border border-gray-200 px-3 py-2.5 z-10 ${stickyBg}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-300 cursor-grab text-xs select-none">⠿</span>
                          <span className={`font-medium text-sm whitespace-nowrap ${total > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{member.name}</span>
                          {total > 0 && (
                            <span className={`text-sm font-semibold tabular-nums shrink-0 ${txtCls}`}>
                              {total}/{effectiveMax}
                            </span>
                          )}
                          {memberTOs.map(t => (
                            <span key={t.id} title={leaveTitle(t)} className={`w-1.5 h-1.5 rounded-full shrink-0 ${leaveDotCls(t)}`} />
                          ))}
                        </div>
                      </td>
                      {orderedProjects.map(project => (
                        <td key={project.id} className="border border-gray-200 px-2 py-1.5 text-center">
                          <HourCell memberId={member.id} projectId={project.id} />
                        </td>
                      ))}
                    </tr>
                  )
                })}

                {/* Totals row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="sticky left-0 bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-600 z-10">
                    {t('planWeek.totalHoursRow')} <span className="text-gray-900 ml-1">{totalHours}h</span>
                  </td>
                  {orderedProjects.map(p => {
                    const ph = getProjectTotal(p.id)
                    return (
                      <td key={p.id} className="border border-gray-200 px-2 py-2.5 text-center text-sm text-gray-700">
                        {ph > 0 ? `${ph}h` : '—'}
                      </td>
                    )
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-2">{t('planWeek.clickToEdit')}</p>
        </div>

      ) : tab === 'project' ? (

        /* ─── PLAN BY PROJECT ─── */
        <div className="space-y-3">
          <ProjectLimitBanners />
          {orderedProjects.map(project => {
            const isOpen = !!expandedProjects[project.id]
            const projectHours = getProjectTotal(project.id)
            const assignedCount = members.filter(m => getAllocHours(m.id, project.id) > 0).length
            const isDragging = dragId === project.id
            const isDragOver = dragOverId === project.id && dragType === 'project'

            return (
              <div
                key={project.id}
                draggable
                onDragStart={e => startDrag(e, project.id, 'project')}
                onDragOver={e => onDragOver(e, project.id)}
                onDrop={e => onDrop(e, project.id, 'project')}
                onDragEnd={onDragEnd}
                className={`bg-white rounded-xl border overflow-hidden transition-all ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-gray-400 shadow-sm' : 'border-gray-200'}`}
              >
                <button
                  onClick={() => setExpandedProjects(prev => ({ ...prev, [project.id]: !prev[project.id] }))}
                  className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <span className="text-gray-300 cursor-grab text-sm select-none">⠿</span>
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color }} />
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-900 text-sm">{project.nickname || project.name}</span>
                    {project.nickname && <span className="ml-2 text-xs text-gray-400 truncate">{project.name}</span>}
                    <LimitMarker projectId={project.id} className="ml-2" />
                    <BudgetMarker projectId={project.id} className="ml-1" />
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {projectHours > 0 && (
                      <span className="text-xs text-gray-500">
                        <span className="font-semibold text-gray-700">{projectHours}h</span> · {t('planWeek.assignedEngineers', {count: assignedCount})}
                      </span>
                    )}
                    <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100">
                    <div className="grid grid-cols-[1fr_120px_1fr] gap-x-4 items-center px-5 py-2 bg-gray-50 border-b border-gray-100">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('planWeek.engineerHeader')}</span>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">{t('planWeek.hoursHeader')}</span>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('planWeek.weeklyUtilization')}</span>
                    </div>
                    {orderedMembers.map(member => {
                      const totalForMember = getTotalHours(member.id)
                      const effectiveMax = getEffectiveMaxHours(member)
                      const memberTOs = getMemberTimeOff(member.id)

                      return (
                        <div
                          key={member.id}
                          className="grid grid-cols-[1fr_120px_1fr] gap-x-4 items-center px-5 py-2.5 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm text-gray-800 truncate">{member.name}</span>
                            {memberTOs.length > 0 && (
                              <span title={memberTOs.map(leaveTitle).join(', ')} className={`w-1.5 h-1.5 rounded-full shrink-0 ${leaveDotCls(memberTOs[0])}`} />
                            )}
                          </div>
                          <div className="flex justify-center">
                            <HourCell memberId={member.id} projectId={project.id} />
                          </div>
                          <UtilBar used={totalForMember} max={effectiveMax} />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          <p className="text-xs text-gray-400 pt-1">{t('planWeek.clickProjectToEdit')}</p>
        </div>

      ) : tab === 'engineer' ? (

        /* ─── PLAN BY ENGINEER ─── */
        <div className="space-y-3">
          {orderedMembers.map(member => {
            const isOpen = !!expandedEngineers[member.id]
            const totalForMember = getTotalHours(member.id)
            const effectiveMax = getEffectiveMaxHours(member)
            const c = utilColor(totalForMember, effectiveMax)
            const memberTOs = getMemberTimeOff(member.id)
            const hasDeduction = effectiveMax < member.max_weekly_hours
            const isDragging = dragId === member.id
            const isDragOver = dragOverId === member.id && dragType === 'member'

            const borderCls = c === 'red' ? 'border-red-200' : c === 'green' ? 'border-emerald-200' : c === 'amber' ? 'border-amber-200' : 'border-gray-200'
            const hoverCls  = c === 'red' ? 'hover:bg-red-50' : c === 'green' ? 'hover:bg-emerald-50' : c === 'amber' ? 'hover:bg-amber-50' : 'hover:bg-gray-50'
            const avatarCls = c === 'red' ? 'bg-red-100 text-red-600' : c === 'green' ? 'bg-emerald-100 text-emerald-600' : c === 'amber' ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-600'
            const barCls    = c === 'red' ? 'bg-red-500' : c === 'green' ? 'bg-emerald-500' : c === 'amber' ? 'bg-amber-400' : 'bg-gray-200'
            const txtCls    = c === 'red' ? 'text-red-500' : c === 'green' ? 'text-emerald-600' : c === 'amber' ? 'text-amber-600' : 'text-gray-400'

            return (
              <div
                key={member.id}
                draggable
                onDragStart={e => startDrag(e, member.id, 'member')}
                onDragOver={e => onDragOver(e, member.id)}
                onDrop={e => onDrop(e, member.id, 'member')}
                onDragEnd={onDragEnd}
                className={`rounded-xl border overflow-hidden bg-white transition-all ${borderCls} ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'shadow-md' : ''}`}
              >
                {/* Engineer header */}
                <button
                  onClick={() => setExpandedEngineers(prev => ({ ...prev, [member.id]: !prev[member.id] }))}
                  className={`w-full flex items-center gap-4 px-5 py-3.5 transition-colors text-left ${hoverCls}`}
                >
                  <span className="text-gray-300 cursor-grab text-sm select-none">⠿</span>
                  {/* Avatar circle */}
                  <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-sm font-bold ${avatarCls}`}>
                    {member.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 text-sm">{member.name}</span>
                      {member.role && <span className="text-xs text-gray-400">{member.role}</span>}
                      {memberTOs.length > 0 && (
                        <div className="flex gap-1">
                          {memberTOs.map(t => (
                            <span key={t.id} title={leaveTitle(t)}
                              className={`text-xs px-1.5 py-0.5 rounded-full ${t.estado === 'pendiente'
                                ? 'border border-dashed border-gray-300 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                              {leaveTitle(t)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Utilization bar in header */}
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="w-32 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-1.5 rounded-full transition-all ${barCls}`}
                          style={{ width: `${effectiveMax > 0 ? Math.min((totalForMember / effectiveMax) * 100, 100) : 0}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${txtCls}`}>
                        {totalForMember}h / {effectiveMax}h
                        {hasDeduction && <span className="text-gray-400 font-normal ml-1">{t('planWeek.avail')}</span>}
                        {c === 'red' && <span className="ml-1">⚠ {t('planWeek.overBy', {amount: (totalForMember - effectiveMax).toFixed(1)})}</span>}
                      </span>
                    </div>
                  </div>

                  <span className="text-gray-400 text-xs shrink-0">{isOpen ? '▲' : '▼'}</span>
                </button>

                {/* Project rows */}
                {isOpen && (
                  <div className="border-t border-gray-100">
                    <div className="grid grid-cols-[1fr_120px_auto] gap-x-4 items-center px-5 py-2 bg-gray-50 border-b border-gray-100">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('planWeek.projectHeader')}</span>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">{t('planWeek.hoursHeader')}</span>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('planWeek.projectTotal')}</span>
                    </div>
                    {orderedProjects.map(project => {
                      const hoursOnProject = getAllocHours(member.id, project.id)
                      const projectTotal = getProjectTotal(project.id)

                      return (
                        <div
                          key={project.id}
                          className="grid grid-cols-[1fr_120px_auto] gap-x-4 items-center px-5 py-2.5 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: project.color }} />
                            <span className="text-sm text-gray-800 truncate">{project.nickname || project.name}</span>
                            {project.nickname && <span className="text-xs text-gray-400 truncate hidden sm:block">{project.name}</span>}
                          </div>
                          <div className="flex justify-center">
                            <HourCell memberId={member.id} projectId={project.id} />
                          </div>
                          <span className="text-xs text-gray-400 text-right">
                            {projectTotal > 0 ? <span className="text-gray-600 font-medium">{projectTotal}h</span> : <span className="text-gray-300">—</span>}
                            <span className="ml-1 hidden sm:inline">{t('planWeek.teamTotal')}</span>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          <p className="text-xs text-gray-400 pt-1">{t('planWeek.clickEngineerToEdit')}</p>
        </div>

      ) : null}

      {/* Bottom clear table button */}
      {!loading && totalHours > 0 && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleClear}
            disabled={clearing}
            className="text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            title={t('planWeek.clearTitle')}
          >
            {clearing ? t('planWeek.clearing') : t('planWeek.clearTable')}
          </button>
        </div>
      )}


      {/* ── Send Plan Modal ── */}
      {showSendModal && (() => {
        const engineersChecked = Object.values(checkedEngineers).filter(Boolean).length
        const pmsValid = Object.entries(checkedPMs)
          .filter(([n, c]) => c && (pmEmails[n] || '').includes('@')).length
        const hasResults = !!sendResult?.results || !!pmResult?.results
        const hasAnyError = !!sendResult?.error || !!pmResult?.error
        const totalSent = (sendResult?.sent_count ?? 0) + (pmResult?.sent_count ?? 0)
        return (
        <Modal title={t('planWeek.sendModal.title')} wide onClose={() => setShowSendModal(false)}>
          <div className="space-y-4">
            {/* Week display */}
            <div className="bg-gray-100 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700">
              {formatWeekRange(week)}
            </div>

            {/* Auth status */}
            {authStatus === null && (
              <div className="text-xs text-gray-400">{t('planWeek.sendModal.checkingEmail')}</div>
            )}
            {authStatus !== null && authStatus.authorized && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                <span>✓</span> {t('planWeek.sendModal.sendingAs', { from: authStatus.from })}
              </div>
            )}
            {authStatus !== null && !authStatus.authorized && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700">
                {t('planWeek.sendModal.notConfigured')}
              </div>
            )}

            {!hasResults && (
              <>
                {/* Engineers section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('planWeek.sendModal.engineers')}</label>
                    {engineerPreview.length > 0 && (
                      <span className="text-xs text-gray-400">
                        {t('planWeek.sendModal.willReceive', {count: engineersChecked})}
                      </span>
                    )}
                  </div>
                  {engineerPreview.length === 0 ? (
                    <div className="text-xs text-gray-400 py-2">{t('planWeek.sendModal.loadingEngineers')}</div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
                      {engineerPreview.map(eng => {
                        const hasEmail = !!eng.email
                        const hasHours = eng.total_hours > 0
                        const checked = !!checkedEngineers[eng.id]
                        return (
                          <label
                            key={eng.id}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${!hasEmail ? 'opacity-40 cursor-not-allowed' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!hasEmail}
                              onChange={() => toggleEngineer(eng.id)}
                              className="rounded border-gray-300 text-gray-900 focus:ring-gray-200"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-800">{eng.name}</span>
                                {!hasHours && hasEmail && (
                                  <span className="text-xs text-gray-400">{t('planWeek.sendModal.noHoursThisWeek')}</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-400 truncate">
                                {eng.email || t('planWeek.sendModal.noEmail')}
                              </div>
                            </div>
                            {eng.total_hours > 0 && (
                              <span className="text-xs font-medium text-gray-700 shrink-0">{eng.total_hours}h</span>
                            )}
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* PMs / POs section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('planWeek.sendModal.pmsPos')}</label>
                    {pmPreview.length > 0 && (
                      <span className="text-xs text-gray-400">
                        {t('planWeek.sendModal.willReceivePM', {count: pmsValid})}
                      </span>
                    )}
                  </div>
                  {pmPreview.length === 0 ? (
                    <div className="text-xs text-gray-400 py-2">{t('planWeek.sendModal.noProjectsWithPM')}</div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
                      {pmPreview.map(pm => {
                        const checked = !!checkedPMs[pm.pm_name]
                        const projCount = pm.projects.length
                        const hours = pm.total_hours
                        const hoursDisp = hours === Math.floor(hours) ? Math.floor(hours) : hours
                        return (
                          <div key={pm.pm_name} className="px-3 py-2.5">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePM(pm.pm_name)}
                                className="rounded border-gray-300 text-gray-900 focus:ring-gray-200"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800 truncate">{pm.pm_name}</div>
                                <div className="text-xs text-gray-400">
                                  {t('planWeek.sendModal.project', {count: projCount})} · {hoursDisp}h {t('planWeek.sendModal.totalLabel')}
                                </div>
                              </div>
                            </div>
                            {/* Email: pick from team members or type custom */}
                            <div className="mt-2 ml-7 flex gap-2">
                              <select
                                value={pmMembers.find(m => m.email === pmEmails[pm.pm_name])?.id ?? ''}
                                onChange={e => {
                                  const m = pmMembers.find(x => x.id === Number(e.target.value))
                                  if (m) setPMEmails(prev => ({ ...prev, [pm.pm_name]: m.email || '' }))
                                  else setPMEmails(prev => ({ ...prev, [pm.pm_name]: '' }))
                                }}
                                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                              >
                                <option value="">{t('planWeek.sendModal.selectTeamMember')}</option>
                                {pmMembers.filter(m => m.email).map(m => (
                                  <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                                ))}
                              </select>
                              <input
                                type="email"
                                placeholder={t('planWeek.sendModal.orTypeEmail')}
                                value={pmEmails[pm.pm_name] || ''}
                                onChange={e => setPMEmails(prev => ({ ...prev, [pm.pm_name]: e.target.value }))}
                                className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                              />
                            </div>
                            <div className="ml-7 mt-1.5 text-xs text-gray-500 space-y-0.5">
                              {pm.projects.map(p => (
                                <div key={p.id} className="flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
                                  <span className="truncate">{p.nickname || p.name}</span>
                                  <span className="text-gray-400">·</span>
                                  <span className="text-gray-400">{t('planWeek.sendModal.engineerCount', {count: p.engineers.length})}, {p.total_hours === Math.floor(p.total_hours) ? Math.floor(p.total_hours) : p.total_hours}h</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Optional message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('planWeek.sendModal.message')} <span className="text-gray-400 font-normal">{t('planWeek.sendModal.messageOptional')}</span>
                  </label>
                  <textarea
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                    value={sendMessage}
                    onChange={e => setSendMessage(e.target.value)}
                    placeholder={t('planWeek.sendModal.notesPlaceholder')}
                  />
                </div>
              </>
            )}

            {/* Results */}
            {hasResults && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  {t('planWeek.sendModal.emailsSent', {count: totalSent})}
                </p>

                {sendResult?.results && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{t('planWeek.sendModal.engineersSection')}</div>
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                      {sendResult.results.map((r, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                          <span className={`text-sm shrink-0 ${
                            r.status === 'sent' ? 'text-emerald-600' :
                            r.status === 'failed' ? 'text-red-500' : 'text-gray-300'
                          }`}>
                            {r.status === 'sent' ? '✓' : r.status === 'failed' ? '✗' : '—'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800">{r.name}</div>
                            {r.status === 'sent' && <div className="text-xs text-gray-400">{r.email}</div>}
                            {r.status === 'skipped_no_email' && <div className="text-xs text-gray-400">{t('planWeek.sendModal.skippedNoEmail')}</div>}
                            {r.status === 'skipped_no_hours' && <div className="text-xs text-gray-400">{t('planWeek.sendModal.skippedNoHours')}</div>}
                            {r.status === 'failed' && <div className="text-xs text-red-400">{r.error}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pmResult?.results && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{t('planWeek.sendModal.pmsPosSection')}</div>
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                      {pmResult.results.map((r, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                          <span className={`text-sm shrink-0 ${r.status === 'sent' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {r.status === 'sent' ? '✓' : '✗'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800">{r.pm_name}</div>
                            <div className="text-xs text-gray-400">{r.email}</div>
                            {r.error && <div className="text-xs text-red-400">{r.error}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Top-level errors */}
            {hasAnyError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 text-sm space-y-1">
                {sendResult?.error && <div className="flex items-center gap-2"><span>✗</span><span>{t('planWeek.sendModal.engineerError', {error: sendResult.error})}</span></div>}
                {pmResult?.error && <div className="flex items-center gap-2"><span>✗</span><span>{t('planWeek.sendModal.pmError', {error: pmResult.error})}</span></div>}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              {!hasResults ? (
                <>
                  <button
                    onClick={handleSend}
                    disabled={sending || (engineersChecked === 0 && pmsValid === 0)}
                    className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {sending ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        {t('planWeek.sendModal.sending')}
                      </>
                    ) : `${t('planWeek.sendModal.send')} (${engineersChecked} ${t('planWeek.sendModal.engineer', {count: engineersChecked})} + ${pmsValid} ${t('planWeek.sendModal.pm', {count: pmsValid})})`}
                  </button>
                  <button
                    onClick={() => setShowSendModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    {t('planWeek.sendModal.cancel')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowSendModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  {t('planWeek.sendModal.close')}
                </button>
              )}
            </div>
          </div>
        </Modal>
        )
      })()}

    </div>
  )
}
